import { Image, merge, Stack, Text, Toggle } from '@fluentui/react';
import React, { useMemo, useState } from 'react';
import closeWhiteIcon from '../../../assets/icons/svg/closeWhite.svg';
import helpIcon from '../../../assets/icons/svg/help.svg';
import { AccessType, useAuth } from '../../../contexts/authContext';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';

const Preferences = (props) => {

    const {
        consentementCollecteDonneesPersonnelles = false,
        consentementDiffusionDonneesPersonnelles = false,
        enabled = false,
        onChangeConsentementCollecteDonneesPersonnelles = (event) => console.log(event),
        onChangeConsentementDiffusionDonneesPersonnelles = (event) => console.log(event),
        onChangeSouhaiteRecevoirOffres = (event) => console.log(event),
        souhaiteRecevoirOffres = false
    } = props;

    const { accessType } = useAuth();
    const { responsiveSize } = useResponsiveSize();

    const [message, setMessage] = useState(undefined);

    const styles = useMemo(
        () => {
            let styles = {
                icon: {
                    root: {
                        cursor: "pointer",
                        paddingBottom: 6,
                        minWidth: 20
                    },
                },
                iconClose: {
                    root: {
                        cursor: "pointer",
                        position: "absolute",
                        right: 13,
                        top: 13
                    },
                },
                outer: {
                    root: {
                        background: "#ECECEC",
                        margin: "26px 0",
                        padding: 10,
                        position: "relative",
                    }
                },
                overlay: {
                    root: {
                        background: "#8A8A8A",
                        inset: 0,
                        opacity: 0.9,
                        padding: "28px 46px",
                        position: "absolute",
                    }
                },
                overlayText: {
                    root: {
                        color: "white",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: "14px",
                        textAlign: "justify",
                    }
                },
                row: {
                    root: {
                        alignItems: "start",
                        flexDirection: "column",
                        gap: 9,
                        justifyContent: "space-between",
                        marginLeft: 5
                    }
                },
                title: {
                    root: {
                        color: "#8A8A8A",
                        paddingBottom: 10,
                    }
                },
                toggle: {
                    pill: {
                        background: enabled ? "red" : "white !important",
                        border: "none",
                        selectors: { '&[aria-checked="false"]': { background: "black" } }
                    },
                    root: {
                        alignSelf: "end",
                        marginLeft: 12
                    },
                    text: {
                        marginLeft: "12px !important",
                        paddingTop: "3px !important",
                        width: 35
                    },
                    thumb: { background: "#ECECEC !important" }
                },
            };

            if (responsiveSize >= ResponsiveSize.medium)
                styles = merge(
                    styles,
                    {
                        outer: { root: { margin: "40px 0" } },
                        row: {
                            root: {
                                alignItems: "center",
                                flexDirection: "row"
                            }
                        },
                        toggle: { root: { alignSelf: "unset" } },
                    }
                );

            return styles;
        },
        [enabled, responsiveSize]
    );

    const consentementCollecteDonneesPersonnellesMessage = useMemo(() => {
        if (accessType === AccessType.prospect) return "Le retrait du consentement au recueil de vos données personnelles entrainera la suppression de vos données personnelles et la fermeture de votre espace personnel. Ce consentement nous est nécessaire pour gérer votre potentielle acquisition chez Eiffage Immobilier. Ces coordonnées serviront à générer les appels de fonds et permettront à notre service client de vous tenir informé des diverses échéances liées à votre achat.";
        else return "Le consentement au recueil de vos données personnelles nous est nécessaire pour gérer votre acquisition chez Eiffage Immobilier. Ces coordonnées serviront à générer les appels de fonds et permettront à notre service client de vous tenir informé des diverses échéances liées à votre achat.";
    }, [accessType]);

    return <Stack styles={styles.outer}>

        <Text styles={styles.title}>Mes préférences</Text>

        <Stack horizontal horizontalAlign="space-between" styles={styles.row}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Text>&#9642;</Text>
                <Text>Je consens au recueil de mes données personnelles par Eiffage Immobilier</Text>
                <Image onClick={(event) => setMessage(consentementCollecteDonneesPersonnellesMessage)} src={helpIcon} styles={styles.icon} />
            </Stack>
            <Toggle
                checked={consentementCollecteDonneesPersonnelles}
                disabled={!enabled || accessType === AccessType.client}
                offText="NON"
                onChange={onChangeConsentementCollecteDonneesPersonnelles}
                onText="OUI"
                styles={styles.toggle}
            />
        </Stack>

        <Stack horizontal horizontalAlign="space-between" styles={styles.row}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Text>&#9642;</Text>
                <Text>Je souhaite recevoir des offres commerciales</Text>
                <Image onClick={(event) => setMessage("Vos données personnelles ne sont pas vendues à des tiers et ne sont utilisées que par Eiffage Immobilier et ses commercialisateurs. En cochant oui, vous pourriez recevoir des communications commerciales (par email et SMS) personnalisées selon votre projet immobilier. En cochant non, vous ne recevrez aucune communication commerciale de notre part.")} src={helpIcon} styles={styles.icon} />
            </Stack>
            <Toggle
                checked={souhaiteRecevoirOffres}
                disabled={!enabled}
                offText="NON"
                onChange={onChangeSouhaiteRecevoirOffres}
                onText="OUI"
                styles={styles.toggle}
            />
        </Stack>

        <Stack styles={styles.row}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Text>&#9642;</Text>
                <Text>Je souhaite être contacté(e) à des fins d'enquête de satisfaction</Text>
                <Image onClick={(event) => setMessage("Dans le cadre de notre démarche qualité et afin d’améliorer nos services, nous menons des enquêtes de satisfaction. Celles-ci ont pour but de recueillir vos retours en fonction de l’état d’avancement de votre projet.")} src={helpIcon} styles={styles.icon} />
            </Stack>
            <Toggle
                checked={consentementDiffusionDonneesPersonnelles}
                disabled={!enabled}
                offText="NON"
                onChange={onChangeConsentementDiffusionDonneesPersonnelles}
                onText="OUI"
                styles={styles.toggle}
            />
        </Stack>

        {!!message &&
            <Stack horizontalAlign="center" verticalAlign="center" styles={styles.overlay}>
                <Image onClick={(event) => setMessage(undefined)} src={closeWhiteIcon} styles={styles.iconClose} />
                <Text styles={styles.overlayText}>{message}</Text>
            </Stack>
        }

    </Stack>;

};

export default Preferences;